/**
 * Importa uma private key ECDSA já existente (em hex) pro keystore.
 * Útil quando o sócio:
 *  - Trocou de device e tem o backup da chave
 *  - Mudou de origem (localhost:5173 → localhost) e o IndexedDB anterior
 *    ficou em outro storage
 *
 * Reusa o mesmo path de criptografia que o `keystore.create`.
 */
import { privateKeyToAccount } from "viem/accounts";
import { type Hex, hexToBytes } from "viem";
import { encryptWithPassword } from "./crypto";
import { saveKey, type StoredKey } from "./storage";
import { keystore } from "./index";

const HEX_KEY_REGEX = /^0x[a-fA-F0-9]{64}$/;

export class InvalidPrivateKeyError extends Error {
  constructor() {
    super("Chave inválida — deve ser 0x seguido de 64 dígitos hexadecimais (32 bytes).");
    this.name = "InvalidPrivateKeyError";
  }
}

export async function importPrivateKey(rawHex: string, password: string): Promise<{ address: string }> {
  const normalized = rawHex.trim().toLowerCase().startsWith("0x")
    ? rawHex.trim().toLowerCase()
    : "0x" + rawHex.trim().toLowerCase();

  if (!HEX_KEY_REGEX.test(normalized)) {
    throw new InvalidPrivateKeyError();
  }

  const priv = normalized as Hex;
  const account = privateKeyToAccount(priv);

  const blob = await encryptWithPassword(hexToBytes(priv), password);
  const stored: StoredKey = {
    ciphertext: blob.ciphertext,
    salt: blob.salt,
    iv: blob.iv,
    address: account.address,
    createdAt: Date.now(),
  };
  await saveKey(stored);

  // Desbloqueia em memória pra próximas requests não precisarem repetir.
  await keystore.unlock(password);

  return { address: account.address };
}
